<template>
  <div class="email-verification-container">
    <!-- Left side -->
    <div v-if="showLeftPanel" class="left-panel">
      <div class="left-content">
        <router-link
          v-if="showLogoLink"
          to="/"
          class="logo-link"
          aria-label="Home page"
        >
          <img
            src="/images/eddy.svg"
            alt=""
            class="filter-white"
            aria-hidden="true"
          />
        </router-link>
        <img
          src="/images/register-illustration.svg"
          alt=""
          class="illustration"
          aria-hidden="true"
        />
        <p class="promotional-text">
          Turn your classroom into an <br />
          online Makerspace today!
        </p>

        <BaseButton
          variant="white"
          class="login-redirect-button"
          @click="goToLogin"
        >
          Already signed up? Sign In here
        </BaseButton>
      </div>
    </div>

    <!-- Right side -->
    <div :class="['right-panel', { 'full-width': !showLeftPanel }]">
      <!-- Email Verified Success Message -->
      <div v-if="isVerified && currentEmail" class="verified-container">
        <div class="verified-content">
          <h1 class="verified-title">Email Verified!</h1>
          <p class="verified-message">
            Your email has been verified successfully and you are now logged in!
            You will be redirected to Eddy in
            <span class="countdown-bold">{{ countdown }}</span> seconds
          </p>
        </div>
      </div>

      <!-- OTP Input -->
      <OTPInput
        v-else-if="currentEmail"
        ref="otpInputRef"
        :is-loading="isLoading"
        @verify="handleVerifyOTP"
        @resend="resendVerificationEmail"
        @validate="handleValidateOTP"
      />

      <!-- Error State -->
      <div v-else class="error-container">
        <p class="error-text">
          {{ errorMessage || "No email found. Please register first." }}
        </p>
        <BaseButton variant="primary" class="back-button" @click="goToLogin">
          Back to Sign In
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import BaseButton from "@/components/common/BaseButton.vue";
import OTPInput from "@/components/OTPInput.vue";
import authService from "@/services/authService";
import localStorageService from "@/services/localStorageService";
import { getSafeAuthRedirect } from "@/utils/authRedirect";
import cookiesService from "@/services/cookiesService";
import { useUserState } from "@/composeables/useUserState";
import { isAxiosError } from "axios";
import {
  onMounted,
  onUnmounted,
  ref,
  defineProps,
  defineEmits,
  defineExpose,
} from "vue";
import { useRouter } from "vue-router";

// Props
const props = defineProps({
  // Controls whether to show the left promotional panel
  showLeftPanel: {
    type: Boolean,
    default: true,
  },
  // Controls whether to show the logo link in top-left
  showLogoLink: {
    type: Boolean,
    default: true,
  },
  // Controls whether to redirect after actions
  enableRedirect: {
    type: Boolean,
    default: true,
  },
  // Email to verify (if not provided, will try to get from localStorage)
  email: {
    type: String,
    default: "",
  },
  // Controls whether to automatically load email from localStorage
  loadEmailFromStorage: {
    type: Boolean,
    default: true,
  },
  // Custom success message to show after email verification
  successMessage: {
    type: String,
    default: "You are now logged in and ready to use your account!",
  },
  // Controls whether to show the success button
  showSuccessButton: {
    type: Boolean,
    default: true,
  },
});

// Emits
const emit = defineEmits([
  "goto-login",
  "resend-success",
  "resend-error",
  "email-verified",
]);

const router = useRouter();
const { updateUserState } = useUserState();
const currentEmail = ref("");
const errorMessage = ref("");
const isLoading = ref(false);
const isVerified = ref(false);
const verificationCheckInterval = ref(null);
const otpInputRef = ref(null);
const countdown = ref(3);
let countdownInterval = null;
const isValidated = ref(false);
const validationResult = ref(null);

const getEmail = () => {
  // First check if email is provided as prop
  if (props.email) {
    currentEmail.value = props.email;
    return;
  }

  // Then try to get from localStorage if enabled (cross-tab access)
  if (props.loadEmailFromStorage) {
    const registeredEmail = localStorage.getItem("registeredEmail");
    if (registeredEmail) {
      currentEmail.value = registeredEmail;
    } else if (props.enableRedirect) {
      // Only redirect if no email found and redirect is enabled
      goToLogin();
    }
  }
};

async function handleValidateOTP(otp) {
  if (!otp || otp.length !== 6) {
    return;
  }

  // Don't validate if already verifying
  if (isLoading.value) {
    return;
  }

  // If already validated with same code, don't validate again
  if (isValidated.value && validationResult.value?.otp === otp) {
    // Update OTP component state based on stored validation result
    if (otpInputRef.value) {
      if (validationResult.value.isValid) {
        otpInputRef.value.setValid();
      } else {
        otpInputRef.value.setError(
          validationResult.value.error || "Invalid code"
        );
      }
    }
    return;
  }

  // Clear previous validation if OTP changed
  if (isValidated.value && validationResult.value?.otp !== otp) {
    isValidated.value = false;
    validationResult.value = null;
  }

  try {
    const response = await authService.verifyEmail(otp, currentEmail.value);

    // Store validation result
    isValidated.value = true;
    validationResult.value = {
      otp,
      response,
      isValid: true,
    };

    // Update OTP component to show valid state
    if (otpInputRef.value) {
      otpInputRef.value.setValid();
    }
  } catch (error) {
    let errorMsg = "Invalid code";

    if (isAxiosError(error)) {
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
    }

    // Store validation result as invalid
    isValidated.value = true;
    validationResult.value = {
      otp,
      isValid: false,
      error: errorMsg,
    };

    // Update OTP component to show error state
    if (otpInputRef.value) {
      otpInputRef.value.setError(errorMsg);
    }
  }
}

async function handleVerifyOTP(otp) {
  if (!otp || otp.length !== 6) {
    if (otpInputRef.value) {
      otpInputRef.value.setError();
    }
    return;
  }

  errorMessage.value = "";
  isLoading.value = true;

  try {
    let response;

    // If already validated and valid, use stored result
    if (
      isValidated.value &&
      validationResult.value?.otp === otp &&
      validationResult.value?.isValid
    ) {
      response = validationResult.value.response;
    } else {
      // Otherwise, validate/verify now
      response = await authService.verifyEmail(otp, currentEmail.value);

      // Store validation result
      isValidated.value = true;
      validationResult.value = {
        otp,
        response,
        isValid: true,
      };
    }

    if (otpInputRef.value) {
      otpInputRef.value.setValid();
    }

    // Handle the response with token data
    if (response.data?.data?.token) {
      const authToken = response.data.data.token;

      // Set the auth cookie with the token
      await cookiesService.setCookie("authToken", { token: authToken });

      // Update global user state in Vuex store (this fetches complete profile)
      await updateUserState();

      console.log(
        "EmailVerificationForm: User logged in automatically after email verification"
      );

      // Set verification status in localStorage for cross-tab communication
      if (currentEmail.value) {
        localStorage.setItem(
          `email_verified_${currentEmail.value}`,
          JSON.stringify({
            verified: true,
            timestamp: Date.now(),
            email: currentEmail.value,
          })
        );

        // Clean up the registered email since verification is complete
        localStorage.removeItem("registeredEmail");
      }
    }

    isVerified.value = true;
    emit("email-verified", { email: currentEmail.value, verified: true });

    startCountdown();
  } catch (error) {
    let errorMsg = "Invalid code";

    if (isAxiosError(error)) {
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
    }

    // Store validation result as invalid
    isValidated.value = true;
    validationResult.value = {
      otp,
      isValid: false,
      error: errorMsg,
    };

    if (otpInputRef.value) {
      otpInputRef.value.setError(errorMsg);
    }
  } finally {
    isLoading.value = false;
  }
}

async function resendVerificationEmail() {
  if (!currentEmail.value) {
    errorMessage.value = "No email found to resend verification";
    emit("resend-error", "No email found");
    return;
  }

  errorMessage.value = "";
  isLoading.value = true;

  // Reset validation state when resending
  isValidated.value = false;
  validationResult.value = null;

  try {
    await authService.resendVerificationEmail(currentEmail.value);
    emit("resend-success", currentEmail.value);

    if (otpInputRef.value) {
      otpInputRef.value.setResendError("");
      otpInputRef.value.reset();
    }
  } catch (error) {
    let errorMsg = "Failed to resend verification email";

    if (isAxiosError(error)) {
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
    }

    if (otpInputRef.value) {
      otpInputRef.value.setResendError(errorMsg);
    }

    errorMessage.value = errorMsg;
    emit("resend-error", errorMsg);
  } finally {
    isLoading.value = false;
  }
}

function goToLogin() {
  emit("goto-login");

  // Default behavior if no custom handler is provided
  if (props.enableRedirect) {
    router.push("/login");
  }
}

const startCountdown = () => {
  countdown.value = 3;

  if (countdownInterval) {
    clearInterval(countdownInterval);
  }

  countdownInterval = setInterval(() => {
    countdown.value--;

    if (countdown.value <= 0) {
      clearInterval(countdownInterval);
      countdownInterval = null;
      continueAfterVerification();
    }
  }, 1000);
};

function continueAfterVerification() {
  const redirectTo = getSafeAuthRedirect(
    localStorage.getItem("postLoginRedirect")
  );
  localStorageService.removeItem("postLoginRedirect");

  try {
    router.push(redirectTo);
  } catch (e) {
    window.location.href = redirectTo;
  }
}

onMounted(() => {
  getEmail();
});

onUnmounted(() => {
  if (verificationCheckInterval.value) {
    clearInterval(verificationCheckInterval.value);
    verificationCheckInterval.value = null;
  }
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
});

// Expose currentEmail for parent components to access
defineExpose({
  currentEmail,
  setEmail: (email) => {
    currentEmail.value = email;
  },
});
</script>

<style scoped>
/* Main Container */
.email-verification-container {
  display: flex;
  height: 100vh;
}

/* Left Panel */
.left-panel {
  width: 50%;
  background-color: var(--primary-blue);
  display: flex;
  align-items: center;
}

.left-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto;
}

.logo-link {
  display: flex;
  gap: 19px;
  margin-top: 40px;
  margin-left: 40px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 50;
}

.illustration {
  width: auto;
  max-width: 100%;
}

.promotional-text {
  color: white;
  font-size: 32px;
  font-weight: 600;
}

.login-redirect-button {
  font-size: var(--fs-big);
  font-weight: 600;
  width: 480px;
  height: 48px;
  border-radius: 12px;
  margin-top: 32px;
}

/* Right Panel */
.right-panel {
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.right-panel.full-width {
  width: 100%;
}

/* Email Verified Success Message */
.verified-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.verified-content {
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.verified-title {
  font-family: "League Spartan", sans-serif;
  font-size: 32px;
  font-weight: 500;
  line-height: 40px;
  margin-bottom: 1rem;
  color: var(--primary-blue);
}

.verified-message {
  font-family: "League Spartan", sans-serif;
  color: #3b4648;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.08px;
  margin-bottom: 2rem;
  max-width: 480px;
}

.countdown-bold {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
}

.continue-button {
  font-size: 18px;
  font-weight: 600;
  width: 300px;
  height: 48px;
  border-radius: 12px;
}

/* Error State */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.error-text {
  color: red;
  font-size: 18px;
  font-weight: 600;
}

.back-button {
  width: 200px;
  height: 48px;
  border-radius: 12px;
  margin-top: 16px;
}

.signup-footer-links {
  cursor: pointer;
  color: #0056b3;
  text-decoration: none;
}

/* Responsive Styles */
@media (max-width: 768px) {
  .left-panel {
    display: none;
  }

  .right-panel {
    width: 100%;
    padding: 16px;
  }

  .verified-content {
    padding: 0 16px;
  }

  .verified-title {
    font-size: var(--fs-xl);
  }

  .verified-message {
    font-size: var(--fs-medium);
  }

  .continue-button {
    width: 100%;
    max-width: 300px;
  }

  .back-button {
    width: 100%;
    max-width: 200px;
  }

  .error-text {
    text-align: center;
    padding: 0 16px;
  }

  .login-redirect-button {
    width: 100%;
    max-width: 480px;
  }
}
</style>
